/** Reopen a Type Set project without pretending the old combined schema is
 * part of the experience. Imports are hostile-input checked by core first. */

import { useRef, useState } from "react";

import { copy, parseProjectFile, type SharedBrief, type TypeSetProjectFile } from "@core/index.js";

import { useAnnounce } from "../app/announcer";
import { useRouter } from "../app/router";
import { useStudio } from "../app/store";
import { isUnrunType } from "../wiring/engines";
import { REMEMBERED_PROJECT_KEY } from "../wiring/exports";

type PagePhase =
  | { kind: "idle" }
  | { kind: "error"; message: string }
  | { kind: "ready"; project: TypeSetProjectFile };

const legacyTypePath = (project: TypeSetProjectFile): "quick" | "deep" => {
  const answers = project.answers.type;
  const hasDeepOnlyAnswer = answers.authoringTool !== "unknown"
    || answers.handoffPaths.length !== 1
    || answers.handoffPaths[0] !== "pdf"
    || answers.viewingContexts.length !== 1
    || answers.viewingContexts[0] !== "mixedUnknown"
    || answers.contentNeeds.length !== 1
    || answers.contentNeeds[0] !== "wordsImages"
    || answers.writingSystems.length !== 1
    || answers.writingSystems[0] !== "latin";
  return hasDeepOnlyAnswer ? "deep" : "quick";
};

const briefFromProject = (project: TypeSetProjectFile): SharedBrief => ({
  route: "type",
  typePath: project.answers.typePath ?? legacyTypePath(project),
  artifactType: project.answers.type.artifactType,
  ...(project.answers.type.otherDecision ? { otherDecision: project.answers.type.otherDecision } : {}),
  existingFontConstraint: project.answers.type.existingFontConstraint,
  ...(project.answers.type.mandatoryFontName ? { mandatoryFontName: project.answers.type.mandatoryFontName } : {}),
  authoringTool: project.answers.type.authoringTool,
  handoffPaths: [...project.answers.type.handoffPaths],
  viewingContexts: [...project.answers.type.viewingContexts],
  density: project.answers.type.density,
  contentNeeds: [...project.answers.type.contentNeeds],
  writingSystems: [...project.answers.type.writingSystems],
  character: project.answers.type.character,
  previewCopy: project.direction.previewCopy,
});

export function ProjectOpenPage() {
  const { dispatch } = useStudio();
  const { navigate } = useRouter();
  const announce = useAnnounce();
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<PagePhase>({ kind: "idle" });
  const [remembered, setRemembered] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(REMEMBERED_PROJECT_KEY);
    } catch {
      return null;
    }
  });

  const restore = (project: TypeSetProjectFile) => {
    const typeResult = isUnrunType(project.direction.type) ? null : project.direction.type;
    if (!typeResult || typeResult.recommendations.length === 0) {
      setPhase({ kind: "error", message: "This file has no finished Type Set direction." });
      return;
    }
    dispatch({
      type: "projectLoaded",
      brief: briefFromProject(project),
      typeResult,
      selectedTypeId: project.direction.selected.typeSystemId ?? typeResult.recommendations[0]?.systemId ?? null,
      projectId: project.projectId,
      projectName: project.direction.name,
      entry: "type",
    });
    announce(`${project.direction.name}. Your type direction is back; nothing left this browser.`);
    navigate("/studio");
  };

  const openText = (text: string) => {
    try {
      const project = parseProjectFile(text);
      if (isUnrunType(project.direction.type) || project.direction.type.recommendations.length === 0) {
        throw new Error("This file has no finished Type Set direction.");
      }
      setPhase({ kind: "ready", project });
    } catch (error) {
      setPhase({ kind: "error", message: error instanceof Error ? error.message : "That file would not parse." });
    }
  };

  const forgetRemembered = () => {
    try {
      window.localStorage.removeItem(REMEMBERED_PROJECT_KEY);
    } catch {
      /* Clearing must never fail loudly. */
    }
    setRemembered(null);
    announce(copy.project.clear);
  };

  const ready = phase.kind === "ready" ? phase.project : null;
  const readyType = ready && !isUnrunType(ready.direction.type) ? ready.direction.type : null;
  const selected = readyType?.recommendations.find((item) => item.systemId === ready?.direction.selected.typeSystemId)
    ?? readyType?.recommendations[0]
    ?? null;

  return (
    <section className="open-shell">
      <header className="page-head">
        <p className="eyebrow">A saved fitting</p>
        <h1 className="display-lg">Bring the type back.</h1>
        <p className="page-lede">Open a project you saved here. Your answers, selected direction, preview copy, sources, and caveats return. No account. No mystery cloud cupboard.</p>
      </header>

      <div className="card" style={{ marginTop: "1.6rem" }}>
        <input
          ref={fileRef}
          type="file"
          accept=".typeset.json,.json,application/json"
          className="visually-hidden"
          aria-label="Choose a Type Set project file"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void file.text().then(openText);
            event.target.value = "";
          }}
        />
        <div className="button-row">
          <button type="button" className="btn btn--primary" onClick={() => fileRef.current?.click()}>Choose a Type Set project</button>
          {remembered ? <button type="button" className="btn" onClick={() => openText(remembered)}>Open the one remembered here</button> : null}
          {remembered ? <button type="button" className="btn btn--ghost" onClick={forgetRemembered}>{copy.project.clear}</button> : null}
        </div>
        {phase.kind === "error" ? <p role="alert" className="truth-note" style={{ color: "var(--caution)", marginTop: "0.9rem" }}>This file was refused: {phase.message}</p> : null}
      </div>

      {ready && selected ? (
        <article className="card saved-project-card" style={{ marginTop: "1.2rem" }}>
          <p className="eyebrow">Ready to reopen</p>
          <h2>{selected.name}</h2>
          <p>{selected.reason}</p>
          <dl className="project-facts">
            <div><dt>Saved</dt><dd>{new Date(ready.updatedAt).toLocaleString()}</dd></div>
            <div><dt>Revision</dt><dd>{ready.revision}</dd></div>
            <div><dt>Type engine</dt><dd>{ready.engineVersions.type}</dd></div>
          </dl>
          <p className="truth-note">Your chosen direction survives. Shuffle history, stars, locks, and fitting-room adjustments were never hidden in the file; start a new fitting when you want those back.</p>
          <button type="button" className="btn btn--primary" onClick={() => restore(ready)}>Open this type direction</button>
        </article>
      ) : null}
    </section>
  );
}
